<template>
  <div id="exchange-modal">
    <font-awesome-icon class="close-icon" :icon="'xmark'" size="4x" @click="cancel" />
    <div class="row">
      <div class="col-12 d-flex justify-content-start title">{{$t('exchange.modal.title')}}</div>
      <div
        class="col-12 d-flex justify-content-start available-credits"
      >{{$t('exchange.modal.availableCredits',{credits: loggedInUserProfile.availableBalance})}}</div>
    </div>
    <div class="row reward-content">
      <div
        v-for="(exchange, index) in exchanges"
        :key="index"
        class="col-6"
        @click="selectExchange(index)"
      >
        <div :class="{'exchange-item': true, 'selected': isItemSelected(index) }">
          <div class="icon">{{exchange.icon}}</div>
          <div class="name">{{exchange.name}}</div>
          <div class="credit">{{exchangeCollectedPrefix(exchange)}}</div>
        </div>
      </div>
      <div class="col-6 d-flex justify-content-center align-items-center" v-if="selectedExchange && selectedExchange.groupable">
        <number-input class="amount-input"
        v-model="amount" :min="loggedInUserProfile.availableBalance > 0 ? 1 : 0"
        :max="loggedInUserProfile.availableBalance"  controls/>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <base-button :disabled="isButtonDisabled" @click="exchange" :text="buttonText" :class="buttonClass" />
      </div>
    </div>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex';
import BaseInput from '@/components/shared/elements/BaseInput';
import BaseButton from '@/components/shared/elements/BaseButton';
import UserImage from '@/components/shared/users/UserImage';

export default {
  name: 'ExchangeModal',
  components: {
    BaseInput,
    BaseButton,
    UserImage,
  },
  props: {},
  data() {
    return {
      buttonNeedConfirm: false,
      transaction: {
        reason: undefined,
      },
      exchanges: [],
      selectedIndex: 0,
      amount: 1,
    };
  },
  created() {
    this.getOpenExchanges();
    this.getExchangeTypes().then((x) => {
      this.exchanges = x;
    });
  },
  methods: {
    ...mapActions({
      getCurrentLeaderboard: 'leaderboard/getCurrentLeaderboard',
      getTransactions: 'leaderboard/getTransactions',
      getExchangeTypes: 'exchanges/getExchangeTypes',
      makeExchange: 'exchanges/makeExchange',
      fetchLoggedInProfile: 'users/fetchLoggedInProfile',
      getOpenExchanges: 'exchanges/getOpenExchanges',
    }),
    reasonChanged(reason) {
      this.reason = reason;
    },
    selectExchange(index) {
      this.selectedIndex = index;
    },
    isItemSelected(index) {
      return this.selectedIndex === index;
    },
    exchange() {
      if (!this.buttonNeedConfirm) {
        this.buttonNeedConfirm = true;
        return;
      }
      const exchangeType = this.exchanges[this.selectedIndex];
      this.makeExchange({
        id: exchangeType._id,
        amount: this.amount,
      }).then((result) => {
        this.fetchLoggedInProfile();
        this.getOpenExchanges();
        if (result._id) {
          this.$swal(
            this.$t('exchange.success.title'),
            this.$t('exchange.success.message'),
            'success',
          );
        } else {
          this.$swal(
            this.$t('exchange.error.title'),
            this.$t('exchange.error.message'),
            'error',
          );
        }
        this.$eventBus.closeModal();
      }).catch((e) => {
        this.$swal(
          this.$t('exchange.error.title'),
          e.message,
          'error',
        );
      });
    },
    cancel() {
      this.$eventBus.closeModal();
    },
    participants(exchange) {
      if (!this.openExchanges) {
        return [];
      }
      const openExchange = this.openExchanges.filter(
        x => x.exchangeType._id === exchange._id,
      )[0];
      if (openExchange) {
        return openExchange.participants || [];
      }
      return [];
    },
    lastParticipants(exchange) {
      const users = this.participants(exchange);
      return users.slice(Math.max(users.length - 5, 0));
    },
    collectedCredits(exchange) {
      const collected = this.participants(exchange).reduce(
        (a, b) => a + b.amount,
        0,
      );
      return collected;
    },
    exchangeCollectedPrefix(exchange) {
      const credits = this.collectedCredits(exchange);
      if (exchange.groupable) {
        return `${credits} / ${exchange.credit}`;
      }
      return exchange.credit;
    },
    moreParticipantsCounter(exchange) {
      return (
        this.participants(exchange).length -
        this.lastParticipants(exchange).length
      );
    },
  },
  computed: {
    ...mapGetters({
      loggedInUserProfile: 'users/loggedInUserProfile',
      openExchanges: 'exchanges/openExchanges',
    }),
    isButtonDisabled() {
      if (this.selectedExchange) {
        if (this.selectedExchange.groupable && this.amount < 1) {
          return true;
        }
        return false;
      }
      return true;
    },
    buttonText() {
      if (this.buttonNeedConfirm) {
        return this.$t('exchange.modal.button.needConfirmText');
      }
      return this.$t('exchange.modal.button.text');
    },
    selectedExchange() {
      return this.exchanges[this.selectedIndex];
    },
    buttonClass() {
      if (this.buttonNeedConfirm) {
        return 'confirm-button pulse animated';
      }
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
  #exchange-modal {
    min-width: 55vw;
    padding: 10px;
    font-size: 14px;
    .row {
      margin: 10px 0;
    }
    .reward-content {
      max-height: 400px;
      overflow-y: scroll;
    }
    .cancel {
      cursor: pointer;
      &:hover {
        color: $secondary-color;
      }
    }
    .title {
      font-size: 24px;
    }
    .available-credits {
      opacity: 0.56;
    }
    .close-icon {
      position: absolute;
      top: 20px;
      right: 20px;
      z-index: 1;
      font-size: 16px;
      color: #aaa;
      cursor: pointer;
      &:hover {
        color: $base-font-color;
      }
    }
    .exchange-item {
      background-color: rgba(0, 0, 0, 0.03);
      border-radius: 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 45px;
      margin: 10px 0;
      cursor: pointer;
      font-size: 14px;
      & > div {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      &.selected {
        color: white;
        background: rgb(51, 128, 195);
        background: linear-gradient(
          90deg,
          rgba(51, 128, 195, 1) 0%,
          rgba(98, 45, 144, 1) 49%
        );
      }
      .icon {
        flex: 2;
        justify-content: flex-start;
        padding-left: 15px;
      }
      .name {
        flex: 5;
        justify-content: flex-start;
      }
      .credit {
        display: block;
        padding-right: 10px;
        text-align:right;
      }
    }
    .amount-input {
      width: 100%;
    }

    .confirm-button {
      >>> .base-button {
        background-color: $secondary-color;
      }
    }
    .slider-col {
      display: flex;
      justify-content: center;
      align-items: center;
      .amount-slider {
        width: 70%;
        >>> .range-slider-fill {
          background-color: $primary-hover-color;
        }
      }
      .amount {
      }
    }
  }
</style>

